import { FC } from 'react';
import { FormikProvider, useFormik } from 'formik';
import { Form } from 'antd';

import { FormikInput, FormikTextArea, openNotification } from 'shared/components/ui';
import { ShareProject } from 'shared/generated-models';
import { shareProjectFormValidationSchema } from 'features/registration';
import { CenterModal, ModalFooter } from 'shared/components';
import { Button, Text } from 'shared/components/ui';
import { useShareProjectMutation } from 'services';

import styles from './index.module.scss';

interface Props {
	onClose: () => void;
	projectId: number;
}

export const ShareProjectModal: FC<Props> = ({ onClose, projectId }: Props) => {
	const [shareProject] = useShareProjectMutation();

	const handleShareProject = async (values: ShareProject) => {
		try {
			await shareProject({ projectId, email: values.email, message: values.message }).unwrap();
			openNotification({ text: `Project shared with ${values.email}` });
			onClose();
		} catch (err) {
			const error = err as { data?: Record<string, any> };
			if (error?.data?.email) {
				formik.setFieldError('email', error.data.email);
			} else {
				formik.setFieldError('email', 'Something went wrong. Try again.');
			}
		}
	};

	const formik = useFormik<ShareProject>({
		initialValues: { email: '' },
		validationSchema: shareProjectFormValidationSchema,
		validateOnMount: false,
		validateOnBlur: false,
		validateOnChange: false,
		enableReinitialize: true,
		onSubmit: values => {
			handleShareProject(values);
		}
	});

	const handleSendInvite = () => {
		formik.handleSubmit();
	};

	return (
		<CenterModal onClose={onClose} icon="user-plus" iconColor="green" title="Share your project">
			<FormikProvider value={formik}>
				<div className={styles.body}>
					<Text variant="inter/14/regular" color="grey">
						Invite teammates by email to view or collaborate on your project.
					</Text>
					<Form className={styles.form} layout="vertical" requiredMark={false} name="registration">
						<FormikInput
							className={styles.input}
							name="email"
							label="Email"
							size="large"
							placeholder="Email"
						/>
						<div className={styles.field}>
							<FormikTextArea name="message" size="small" placeholder="Message" />
						</div>
					</Form>
				</div>
				<ModalFooter>
					<Button type="ghost" onClick={onClose}>
						Cancel
					</Button>
					<Button
						className={styles.button}
						type="primary"
						block
						onClick={handleSendInvite}
						disabled={!formik.isValid}>
						Send an Invite
					</Button>
				</ModalFooter>
			</FormikProvider>
		</CenterModal>
	);
};
